<template>
  <div>
    <div class="crm-page-header">
      <div>
        <h2 class="font-weight-bolder mb-1">Configurações CRM</h2>
        <div class="text-muted">
          Funis, etapas, automações, campos comerciais e cadastros auxiliares
        </div>
      </div>
      <div class="crm-header-actions">
        <button
          class="btn btn-light btn-sm"
          type="button"
          :disabled="loading"
          @click="loadAll"
        >
          Atualizar
        </button>
        <button class="btn btn-dark btn-sm" type="button" @click="novoFunil">
          Novo funil
        </button>
        <button
          class="btn btn-light-primary btn-sm"
          type="button"
          :disabled="saving"
          @click="abrirModalPreConfiguracoes"
        >
          Escolher pre-configuração
        </button>
        <button
          class="btn btn-light-info btn-sm"
          type="button"
          :disabled="loadingAtribuicoes"
          @click="abrirModalAtribuicoes"
        >
          Definir atribuições
        </button>
      </div>
    </div>

    <b-tabs content-class="mt-5" nav-class="crm-config-tabs">
      <b-tab title="Funis e etapas" active>
        <div v-if="loading" class="card card-custom">
          <div class="card-body text-muted">Carregando configurações...</div>
        </div>

        <b-row v-else>
          <b-col lg="4" class="mb-5">
            <div class="crm-panel">
              <div class="crm-panel-header">
                <h3>Funis</h3>
                <button
                  class="btn btn-light-primary btn-sm"
                  type="button"
                  @click="novoFunil"
                >
                  Adicionar
                </button>
              </div>

              <button
                v-for="funil in funisOrdenados"
                :key="funil.id_crm_funil"
                type="button"
                class="crm-list-item"
                :class="{ active: funil.id_crm_funil === selectedFunilId }"
                @click="selectedFunilId = funil.id_crm_funil"
              >
                <span>
                  <strong>{{ funil.nome }}</strong>
                  <small v-if="funil.descricao">{{ funil.descricao }}</small>
                </span>
                <span class="crm-badges">
                  <b-badge v-if="funil.padrao" variant="primary"
                    >Padrão</b-badge
                  >
                  <b-badge v-if="!funil.ativo" variant="secondary"
                    >Inativo</b-badge
                  >
                </span>
              </button>

              <div v-if="funisOrdenados.length === 0" class="crm-empty">
                <strong>Nenhum funil cadastrado</strong>
                <button
                  class="btn btn-dark btn-sm mt-3"
                  type="button"
                  @click="novoFunil"
                >
                  Criar funil
                </button>
              </div>
            </div>
          </b-col>

          <b-col lg="8">
            <div class="crm-panel">
              <div class="crm-panel-header">
                <div>
                  <h3>
                    {{ funilSelecionado ? funilSelecionado.nome : "Etapas" }}
                  </h3>
                  <div v-if="funilSelecionado" class="text-muted">
                    Ordem, probabilidade e cor no Kanban
                  </div>
                </div>
                <div v-if="funilSelecionado" class="crm-panel-actions">
                  <button
                    class="btn btn-light btn-sm"
                    type="button"
                    @click="editarFunil(funilSelecionado)"
                  >
                    Editar funil
                  </button>
                  <button
                    class="btn btn-light-danger btn-sm"
                    type="button"
                    :disabled="saving"
                    @click="apagarFunil(funilSelecionado)"
                  >
                    Apagar funil
                  </button>
                  <button
                    class="btn btn-light-primary btn-sm"
                    type="button"
                    @click="novaEtapa(funilSelecionado)"
                  >
                    Nova etapa
                  </button>
                </div>
              </div>

              <div v-if="!funilSelecionado" class="crm-empty">
                Selecione ou crie um funil.
              </div>

              <div
                v-else-if="etapasSelecionadas.length === 0"
                class="crm-empty"
              >
                <strong>Nenhuma etapa cadastrada</strong>
                <button
                  class="btn btn-dark btn-sm mt-3"
                  type="button"
                  @click="novaEtapa(funilSelecionado)"
                >
                  Criar etapa
                </button>
              </div>

              <div v-else class="crm-stage-list">
                <div
                  v-for="etapa in etapasSelecionadas"
                  :key="etapa.id_crm_etapa"
                  class="crm-stage-row"
                >
                  <span
                    class="crm-color"
                    :style="{ backgroundColor: etapa.cor || '#3699ff' }"
                  ></span>
                  <div class="crm-stage-main">
                    <strong>{{ etapa.nome }}</strong>
                    <small>
                      Ordem {{ etapa.ordem || 0 }} ·
                      {{ etapa.probabilidade || 0 }}%
                    </small>
                    <small v-if="etapaAutomacaoResumo(etapa)">
                      {{ etapaAutomacaoResumo(etapa) }}
                    </small>
                    <small v-if="etapaCamposObrigatoriosResumo(etapa)">
                      {{ etapaCamposObrigatoriosResumo(etapa) }}
                    </small>
                  </div>
                  <div class="crm-stage-actions">
                    <b-badge :variant="etapa.ativo ? 'success' : 'secondary'">
                      {{ etapa.ativo ? "Ativa" : "Inativa" }}
                    </b-badge>
                    <button
                      class="btn btn-light btn-sm"
                      type="button"
                      @click="editarEtapa(etapa)"
                    >
                      Editar
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="funilSelecionado" class="crm-access-box">
                <div class="crm-access-header">
                  <div>
                    <h4>Usuários com acesso</h4>
                    <small>
                      Quem pode visualizar este funil. O gestor continua vendo
                      todos os funis pela permissão de configuração.
                    </small>
                  </div>
                  <button
                    class="btn btn-dark btn-sm"
                    type="button"
                    :disabled="saving"
                    @click="salvarAcessosFunil"
                  >
                    Salvar acessos
                  </button>
                </div>

                <div
                  v-if="usuarioOptionsSemTodos.length === 0"
                  class="crm-empty compact"
                >
                  Nenhum usuário encontrado para configurar acesso.
                </div>

                <b-form-checkbox-group
                  v-else
                  v-model="acessosFunilForm"
                  :options="usuarioOptionsSemTodos"
                  stacked
                  class="crm-access-options"
                />
              </div>
            </div>
          </b-col>
        </b-row>
      </b-tab>

      <b-tab title="Fontes">
        <div class="crm-panel">
          <div class="crm-panel-header">
            <h3>Fontes de oportunidade</h3>
            <button
              class="btn btn-dark btn-sm"
              type="button"
              @click="novaFonte"
            >
              Nova fonte
            </button>
          </div>

          <div v-if="fontes.length === 0" class="crm-empty">
            Nenhuma fonte cadastrada. Crie fontes para identificar de onde as
            oportunidades chegam.
          </div>

          <div v-else class="crm-chip-grid">
            <button
              v-for="fonte in fontes"
              :key="fonte.id_crm_fonte"
              type="button"
              class="crm-chip"
              @click="editarFonte(fonte)"
            >
              <span>{{ fonte.nome }}</span>
              <b-badge :variant="fonte.ativo ? 'success' : 'secondary'">
                {{ fonte.ativo ? "Ativa" : "Inativa" }}
              </b-badge>
            </button>
          </div>
        </div>
      </b-tab>

      <b-tab title="Tipos de tarefa">
        <div class="crm-panel">
          <div class="crm-panel-header">
            <h3>Tipos de tarefa</h3>
            <button
              class="btn btn-dark btn-sm"
              type="button"
              @click="novoTipoTarefa"
            >
              Novo tipo
            </button>
          </div>

          <div v-if="tiposTarefa.length === 0" class="crm-empty">
            Nenhum tipo de tarefa cadastrado. Crie tipos para padronizar as
            próximas ações do time.
          </div>

          <div v-else class="crm-chip-grid">
            <button
              v-for="tipo in tiposTarefa"
              :key="tipo.id_crm_tipo_tarefa"
              type="button"
              class="crm-chip"
              @click="editarTipoTarefa(tipo)"
            >
              <span>{{ tipo.nome }}</span>
              <b-badge :variant="tipo.ativo ? 'success' : 'secondary'">
                {{ tipo.ativo ? "Ativo" : "Inativo" }}
              </b-badge>
            </button>
          </div>
        </div>
      </b-tab>

      <b-tab title="Temperaturas">
        <div class="crm-panel">
          <div class="crm-panel-header">
            <h3>Temperaturas e qualificações</h3>
            <button
              class="btn btn-dark btn-sm"
              type="button"
              @click="novaTemperatura"
            >
              Nova temperatura
            </button>
          </div>

          <div v-if="temperaturas.length === 0" class="crm-empty">
            Nenhuma temperatura cadastrada. Crie qualificações para priorizar
            oportunidades no funil.
          </div>

          <div v-else class="crm-chip-grid">
            <button
              v-for="temperatura in temperaturas"
              :key="temperatura.id_crm_temperatura"
              type="button"
              class="crm-chip"
              @click="editarTemperatura(temperatura)"
            >
              <span>
                <i
                  class="crm-dot"
                  :style="{ backgroundColor: temperatura.cor || '#3699ff' }"
                ></i>
                {{ temperatura.nome }}
              </span>
              <span class="crm-badges">
                <b-badge variant="light"
                  >Peso {{ temperatura.peso || 0 }}</b-badge
                >
                <b-badge :variant="temperatura.ativo ? 'success' : 'secondary'">
                  {{ temperatura.ativo ? "Ativa" : "Inativa" }}
                </b-badge>
              </span>
            </button>
          </div>
        </div>
      </b-tab>

      <b-tab title="Campos comerciais">
        <div class="crm-panel">
          <div class="crm-panel-header">
            <h3>Campos comerciais opcionais</h3>
            <button
              class="btn btn-dark btn-sm"
              type="button"
              @click="novoCampoComercial"
            >
              Novo campo
            </button>
          </div>

          <div v-if="camposComerciais.length === 0" class="crm-empty">
            Nenhum campo opcional cadastrado. Use campos comerciais para adaptar
            o CRM ao seu processo sem mexer no cadastro padrão.
          </div>

          <div v-else class="crm-chip-grid">
            <button
              v-for="campo in camposComerciais"
              :key="campo.id_crm_campo_comercial"
              type="button"
              class="crm-chip"
              @click="editarCampoComercial(campo)"
            >
              <span>
                <strong>{{ campo.nome }}</strong>
                <small>
                  {{ tipoCampoLabel(campo.tipo) }} · Ordem
                  {{ campo.ordem || 0 }}
                </small>
              </span>
              <span class="crm-badges">
                <b-badge v-if="campo.obrigatorio" variant="warning">
                  Obrigatório
                </b-badge>
                <b-badge v-if="campo.visivel_kanban" variant="primary">
                  Kanban
                </b-badge>
                <b-badge :variant="campo.ativo ? 'success' : 'secondary'">
                  {{ campo.ativo ? "Ativo" : "Inativo" }}
                </b-badge>
              </span>
            </button>
          </div>
        </div>
      </b-tab>

      <b-tab title="Automações">
        <b-row>
          <b-col lg="4" class="mb-5">
            <div class="crm-panel">
              <div class="crm-panel-header">
                <div>
                  <h3>Automações</h3>
                  <div class="text-muted">Regras ativas e rascunhos</div>
                </div>
                <button
                  class="btn btn-dark btn-sm"
                  type="button"
                  @click="novaAutomacao"
                >
                  Nova
                </button>
              </div>

              <div v-if="automacoes.length === 0" class="crm-empty">
                <strong>Nenhuma automação cadastrada</strong>
                <span>Crie regras para padronizar o fluxo comercial.</span>
              </div>

              <template v-else>
                <div
                  v-for="automacao in automacoes"
                  :key="automacao.id_crm_automacao"
                  class="crm-list-item crm-list-item--automation"
                  :class="{
                    active:
                      automacao.id_crm_automacao ===
                      automacaoForm.id_crm_automacao,
                  }"
                >
                  <button
                    class="crm-list-item-main"
                    type="button"
                    @click="editarAutomacao(automacao)"
                  >
                    <span>
                      <strong>{{ automacao.nome }}</strong>
                      <small>{{ automacaoGatilhoResumo(automacao) }}</small>
                      <small>{{ automacaoAcaoResumo(automacao) }}</small>
                      <small>Prioridade {{ automacao.prioridade || 0 }}</small>
                      <small v-if="automacao.escopo === 'preset'">
                        Regra do preset padrão
                      </small>
                      <small v-if="automacao.ultima_execucao_em">
                        Última execução:
                        {{ formatDateTime(automacao.ultima_execucao_em) }}
                      </small>
                    </span>
                  </button>
                  <div class="crm-automation-controls">
                    <span class="crm-badges">
                      <b-badge
                        v-if="automacao.escopo === 'preset'"
                        variant="info"
                      >
                        Preset
                      </b-badge>
                      <b-badge
                        :variant="automacao.ativo ? 'success' : 'secondary'"
                      >
                        {{ automacao.ativo ? "Ativa" : "Inativa" }}
                      </b-badge>
                    </span>
                    <div class="crm-automation-buttons">
                      <button
                        class="btn btn-light btn-sm"
                        type="button"
                        :disabled="saving"
                        @click="editarAutomacao(automacao)"
                      >
                        Editar
                      </button>
                      <button
                        class="btn btn-light btn-sm"
                        type="button"
                        :disabled="saving"
                        @click="alternarStatusAutomacao(automacao)"
                      >
                        {{ automacao.ativo ? "Inativar" : "Ativar" }}
                      </button>
                      <button
                        class="btn btn-light-danger btn-sm"
                        type="button"
                        :disabled="saving"
                        @click="excluirAutomacao(automacao)"
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </b-col>

          <b-col lg="8">
            <div class="crm-panel mb-5">
              <div class="crm-panel-header">
                <div>
                  <h3>
                    {{
                      automacaoForm.id_crm_automacao
                        ? "Editar automação"
                        : "Nova automação"
                    }}
                  </h3>
                  <div class="text-muted">
                    Gatilho, condições e ações simples
                  </div>
                </div>
                <b-badge
                  :variant="automacaoForm.ativo ? 'success' : 'secondary'"
                >
                  {{ automacaoForm.ativo ? "Ativa" : "Inativa" }}
                </b-badge>
              </div>

              <b-alert show variant="light" class="crm-config-alert">
                As rotas desta aba usam a permissão
                <strong>crm/configuracoes</strong>. A simulação não altera
                oportunidades, tarefas ou timeline.
              </b-alert>

              <b-form @submit.prevent="salvarAutomacao">
                <b-row>
                  <b-col md="8">
                    <b-form-group label="Nome">
                      <b-form-input
                        v-model="automacaoForm.nome"
                        maxlength="120"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="4">
                    <b-form-group label="Prioridade">
                      <b-form-input
                        v-model.number="automacaoForm.prioridade"
                        type="number"
                        min="0"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-form-group label="Descrição">
                  <b-form-textarea v-model="automacaoForm.descricao" rows="2" />
                </b-form-group>

                <b-row>
                  <b-col md="7">
                    <b-form-group label="Gatilho">
                      <b-form-select
                        v-model="automacaoForm.gatilho"
                        :options="automacaoGatilhoOptions"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="3">
                    <b-form-group label="Máx. execuções">
                      <b-form-input
                        v-model.number="automacaoForm.max_execucoes_por_evento"
                        type="number"
                        min="1"
                        max="10"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="2" class="d-flex align-items-center">
                    <b-form-checkbox v-model="automacaoForm.ativo">
                      Ativa
                    </b-form-checkbox>
                  </b-col>
                </b-row>

                <div class="crm-builder-section">
                  <div class="crm-builder-title">
                    <h4>Condições</h4>
                    <button
                      class="btn btn-light-primary btn-sm"
                      type="button"
                      @click="adicionarCondicao"
                    >
                      Adicionar
                    </button>
                  </div>

                  <div
                    v-if="automacaoForm.condicoes.length === 0"
                    class="crm-empty compact"
                  >
                    Sem condições. A automação roda para qualquer evento deste
                    gatilho.
                  </div>

                  <div
                    v-for="(condicao, index) in automacaoForm.condicoes"
                    :key="`condicao-${index}`"
                    class="crm-builder-row"
                  >
                    <b-form-select
                      v-model="condicao.campo"
                      :options="automacaoCondicaoOptions"
                    />
                    <b-form-select
                      v-model="condicao.operador"
                      :options="automacaoOperadorOptions"
                    />
                    <b-form-input
                      v-model="condicao.valor"
                      placeholder="Valor esperado"
                    />
                    <button
                      class="btn btn-light-danger btn-sm"
                      type="button"
                      @click="removerCondicao(index)"
                    >
                      Remover
                    </button>
                  </div>
                </div>

                <div class="crm-builder-section">
                  <div class="crm-builder-title">
                    <h4>Ações</h4>
                    <button
                      class="btn btn-light-primary btn-sm"
                      type="button"
                      @click="adicionarAcao"
                    >
                      Adicionar
                    </button>
                  </div>

                  <div
                    v-for="(acao, index) in automacaoForm.acoes"
                    :key="`acao-${index}`"
                    class="crm-action-builder"
                  >
                    <div class="crm-builder-row">
                      <b-form-select
                        v-model="acao.acao"
                        :options="automacaoAcaoOptions"
                        @change="aoAlterarAcao(acao)"
                      />
                      <button
                        class="btn btn-light-danger btn-sm"
                        type="button"
                        :disabled="automacaoForm.acoes.length === 1"
                        @click="removerAcao(index)"
                      >
                        Remover
                      </button>
                    </div>

                    <div class="crm-automation-editor">
                      <div v-if="acaoTemDestino(acao)" class="crm-action-box">
                        <div class="crm-action-label">Destino do fluxo</div>
                        <b-row>
                          <b-col md="6">
                            <b-form-group label="Funil de destino">
                              <b-form-select
                                v-model="acao.parametros.id_crm_funil_destino"
                                :options="automacaoFunilOptionsComVazio"
                                @change="atualizarParametrosTexto(acao)"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col md="6">
                            <b-form-group label="Etapa de destino">
                              <b-form-select
                                v-model="acao.parametros.id_crm_etapa_destino"
                                :options="
                                  automacaoEtapaOptionsPorFunil(
                                    acao.parametros.id_crm_funil_destino
                                  )
                                "
                                @change="atualizarParametrosTexto(acao)"
                              />
                            </b-form-group>
                          </b-col>
                        </b-row>
                      </div>

                      <div
                        v-if="acaoTemAtribuicao(acao)"
                        class="crm-action-box"
                      >
                        <div class="crm-action-label">Atribuir card</div>
                        <b-row>
                          <b-col md="6">
                            <b-form-group :label="labelAtribuicaoAcao(acao)">
                              <b-form-select
                                v-model="acao.parametros.id_usuario_tecnico"
                                :options="automacaoUsuarioOptionsComVazio"
                                @change="atualizarParametrosTexto(acao)"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col md="6">
                            <b-form-group label="Regra de responsável">
                              <b-form-input
                                v-model="acao.parametros.responsavel"
                                placeholder="Ex.: tecnico_apresentacao"
                                @input="atualizarParametrosTexto(acao)"
                              />
                            </b-form-group>
                          </b-col>
                        </b-row>
                        <small class="text-muted">
                          Isso define quem recebe ou passa a operar o card. O
                          acesso ao funil é configurado separadamente abaixo.
                        </small>
                      </div>

                      <div
                        v-if="acao.acao === 'alterar_responsavel'"
                        class="crm-action-box"
                      >
                        <div class="crm-action-label">Atribuir card</div>
                        <b-form-group label="Novo responsável">
                          <b-form-select
                            v-model="acao.parametros.id_usuario"
                            :options="automacaoUsuarioOptionsComVazio"
                            @change="atualizarParametrosTexto(acao)"
                          />
                        </b-form-group>
                      </div>

                      <div
                        v-if="acaoTemLiberacaoFunil(acao)"
                        class="crm-action-box"
                      >
                        <div class="crm-action-label">Liberar funil</div>
                        <b-form-checkbox
                          v-model="
                            acao.parametros.conceder_acesso_funil_tecnico
                          "
                          class="mb-3"
                          @change="atualizarParametrosTexto(acao)"
                        >
                          Dar acesso ao funil para o usuário definido na regra
                        </b-form-checkbox>
                        <b-form-group label="Funil liberado">
                          <b-form-select
                            v-model="acao.parametros.id_crm_funil_tecnico"
                            :options="automacaoFunilOptionsComVazio"
                            @change="atualizarParametrosTexto(acao)"
                          />
                        </b-form-group>
                      </div>

                      <div
                        v-if="acao.acao === 'exigir_campo_antes_mover'"
                        class="crm-action-box"
                      >
                        <div class="crm-action-label">Campo obrigatório</div>
                        <b-row>
                          <b-col md="5">
                            <b-form-group label="Campo">
                              <b-form-select
                                v-model="acao.parametros.id_crm_campo_comercial"
                                :options="automacaoCampoOptionsComVazio"
                                @change="sincronizarCampoObrigatorioAcao(acao)"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col md="7">
                            <b-form-group label="Mensagem">
                              <b-form-input
                                v-model="acao.parametros.mensagem"
                                @input="atualizarParametrosTexto(acao)"
                              />
                            </b-form-group>
                          </b-col>
                        </b-row>
                      </div>

                      <div
                        v-if="acao.acao === 'criar_tarefa'"
                        class="crm-action-box"
                      >
                        <div class="crm-action-label">Tarefa criada</div>
                        <b-row>
                          <b-col md="5">
                            <b-form-group label="Título">
                              <b-form-input
                                v-model="acao.parametros.titulo"
                                @input="atualizarParametrosTexto(acao)"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col md="4">
                            <b-form-group label="Tipo">
                              <b-form-input
                                v-model="acao.parametros.tipo_tarefa"
                                placeholder="ligacao, tarefa..."
                                @input="atualizarParametrosTexto(acao)"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col md="3">
                            <b-form-group label="Responsável">
                              <b-form-select
                                v-model="acao.parametros.id_usuario"
                                :options="automacaoUsuarioOptionsComVazio"
                                @change="atualizarParametrosTexto(acao)"
                              />
                            </b-form-group>
                          </b-col>
                        </b-row>
                      </div>

                      <div
                        v-if="acao.acao === 'atualizar_cliente'"
                        class="crm-action-box"
                      >
                        <div class="crm-action-label">Dados obrigatórios</div>
                        <b-form-group
                          label="Campos obrigatórios para salvar cliente"
                          description="Separe chaves por vírgula. Ex.: cnpj, email_cliente, endereco_cliente"
                        >
                          <b-form-input
                            v-model="acao.parametros.campos_obrigatorios_texto"
                            @input="atualizarCamposObrigatoriosAcao(acao)"
                          />
                        </b-form-group>
                      </div>

                      <div
                        v-if="acao.acao === 'enviar_link_agenda'"
                        class="crm-action-box"
                      >
                        <div class="crm-action-label">Agenda por link</div>
                        <b-row>
                          <b-col md="7">
                            <b-form-group label="Tipo da tarefa">
                              <b-form-input
                                v-model="acao.parametros.tipo_tarefa"
                                @input="atualizarParametrosTexto(acao)"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col md="5" class="d-flex align-items-center">
                            <b-form-checkbox
                              v-model="
                                acao.parametros.obrigatorio_para_concluir
                              "
                              @change="atualizarParametrosTexto(acao)"
                            >
                              Obrigatório para concluir
                            </b-form-checkbox>
                          </b-col>
                        </b-row>
                      </div>

                      <div
                        v-if="
                          acao.acao === 'criar_timeline' ||
                          acao.acao === 'notificar_usuario'
                        "
                        class="crm-action-box"
                      >
                        <div class="crm-action-label">Mensagem</div>
                        <b-row>
                          <b-col md="8">
                            <b-form-group label="Descrição">
                              <b-form-input
                                v-model="acao.parametros.descricao"
                                @input="atualizarParametrosTexto(acao)"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col
                            v-if="acao.acao === 'notificar_usuario'"
                            md="4"
                          >
                            <b-form-group label="Usuário">
                              <b-form-select
                                v-model="acao.parametros.id_usuario"
                                :options="automacaoUsuarioOptionsComVazio"
                                @change="atualizarParametrosTexto(acao)"
                              />
                            </b-form-group>
                          </b-col>
                        </b-row>
                      </div>

                      <details class="crm-advanced-json">
                        <summary>JSON avançado</summary>
                        <b-form-textarea
                          v-model="acao.parametros_texto"
                          rows="3"
                          @blur="sincronizarParametrosTexto(acao, true)"
                        />
                      </details>
                    </div>
                  </div>
                </div>

                <div class="crm-builder-footer">
                  <button
                    class="btn btn-light"
                    type="button"
                    @click="novaAutomacao"
                  >
                    Limpar
                  </button>
                  <button class="btn btn-dark" type="submit" :disabled="saving">
                    Salvar automação
                  </button>
                </div>
              </b-form>
            </div>

            <b-row>
              <b-col lg="5" class="mb-5">
                <div class="crm-panel h-100">
                  <div class="crm-panel-header">
                    <h3>Simular</h3>
                  </div>
                  <b-form-group label="ID da oportunidade">
                    <b-form-input
                      v-model.number="automacaoSimulacao.id_crm_oportunidade"
                      type="number"
                      min="1"
                    />
                  </b-form-group>
                  <button
                    class="btn btn-light-primary btn-block"
                    type="button"
                    :disabled="saving || !automacaoForm.gatilho"
                    @click="simularAutomacao"
                  >
                    Simular gatilho
                  </button>
                  <div
                    v-if="automacaoSimulacao.resultado"
                    class="crm-simulation-result"
                  >
                    <strong>Resultado</strong>
                    <small>
                      Executadas:
                      {{ automacaoSimulacao.resultado.executadas || 0 }} ·
                      Ignoradas:
                      {{ automacaoSimulacao.resultado.ignoradas || 0 }}
                    </small>
                  </div>
                </div>
              </b-col>
              <b-col lg="7" class="mb-5">
                <div class="crm-panel h-100">
                  <div class="crm-panel-header">
                    <h3>Histórico de execuções</h3>
                    <button
                      class="btn btn-light btn-sm"
                      type="button"
                      @click="loadAutomacaoExecucoes"
                    >
                      Atualizar
                    </button>
                  </div>

                  <div
                    v-if="automacaoExecucoes.length === 0"
                    class="crm-empty compact"
                  >
                    Nenhuma execução registrada.
                  </div>

                  <div v-else class="crm-execution-list">
                    <div
                      v-for="execucao in automacaoExecucoes"
                      :key="execucao.id_crm_automacao_execucao"
                      class="crm-execution-row"
                    >
                      <div>
                        <strong>{{ execucao.automacao || "Automação" }}</strong>
                        <small>
                          {{ automacaoGatilhoLabel(execucao.gatilho) }} ·
                          {{ formatDateTime(execucao.created_at) }}
                        </small>
                        <small v-if="execucao.erro">{{ execucao.erro }}</small>
                      </div>
                      <b-badge
                        :variant="execucaoStatusVariant(execucao.status)"
                      >
                        {{ execucao.status }}
                      </b-badge>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-tab>

      <b-tab title="Motivos de perda">
        <div class="crm-panel">
          <div class="crm-panel-header">
            <h3>Motivos de perda</h3>
            <button
              class="btn btn-dark btn-sm"
              type="button"
              @click="novoMotivo"
            >
              Novo motivo
            </button>
          </div>

          <div v-if="motivos.length === 0" class="crm-empty">
            Nenhum motivo cadastrado. Crie motivos para entender por que
            oportunidades são perdidas.
          </div>

          <div v-else class="crm-chip-grid">
            <button
              v-for="motivo in motivos"
              :key="motivo.id_crm_motivo_perda"
              type="button"
              class="crm-chip"
              @click="editarMotivo(motivo)"
            >
              <span>{{ motivo.nome }}</span>
              <b-badge :variant="motivo.ativo ? 'success' : 'secondary'">
                {{ motivo.ativo ? "Ativo" : "Inativo" }}
              </b-badge>
            </button>
          </div>
        </div>
      </b-tab>
    </b-tabs>

    <b-modal
      v-model="modalPreConfiguracoes"
      title="Escolher pre-configuração"
      size="lg"
      hide-footer
      modal-class="crm-presets-modal"
    >
      <div class="crm-presets">
        <div class="crm-assignments-intro">
          <div>
            <strong>Pré-configurações CRM</strong>
            <span>
              Escolha um modelo para instalar ou atualizar sem apagar os funis
              que já existem.
            </span>
          </div>
          <button
            class="btn btn-light btn-sm"
            type="button"
            :disabled="loadingPreConfiguracoes"
            @click="loadPreConfiguracoes"
          >
            Atualizar
          </button>
        </div>

        <div v-if="loadingPreConfiguracoes" class="crm-empty compact">
          Carregando pré-configurações...
        </div>

        <div v-else class="crm-presets-grid">
          <section
            v-for="preset in preConfiguracoes"
            :key="preset.chave"
            class="crm-preset-card"
          >
            <div class="crm-preset-card__header">
              <div>
                <h4>{{ preset.nome }}</h4>
                <p>{{ preset.descricao }}</p>
              </div>
              <b-badge :variant="variantStatusPreConfiguracao(preset)">
                {{ labelStatusPreConfiguracao(preset) }}
              </b-badge>
            </div>

            <ul>
              <li v-for="item in preset.itens || []" :key="item">
                {{ item }}
              </li>
            </ul>

            <button
              class="btn btn-dark btn-sm"
              type="button"
              :disabled="saving || preset.status_instalacao === 'instalado'"
              @click="instalarPreConfiguracao(preset)"
            >
              {{ labelAcaoPreConfiguracao(preset) }}
            </button>
          </section>
        </div>

        <div class="crm-modal-actions">
          <button
            class="btn btn-light"
            type="button"
            :disabled="saving"
            @click="modalPreConfiguracoes = false"
          >
            Fechar
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      v-model="modalAtribuicoes"
      title="Definir atribuições"
      size="xl"
      hide-footer
      modal-class="crm-assignments-modal"
    >
      <div class="crm-assignments">
        <div class="crm-assignments-intro">
          <div>
            <strong>Preset software-house</strong>
            <span>
              Escolha quem atua em cada papel. Um usuário pode aparecer em mais
              de uma atribuição.
            </span>
          </div>
          <button
            class="btn btn-light btn-sm"
            type="button"
            :disabled="loadingAtribuicoes"
            @click="loadAtribuicoes"
          >
            Atualizar
          </button>
        </div>

        <div v-if="loadingAtribuicoes" class="crm-empty compact">
          Carregando atribuições...
        </div>

        <div
          v-else-if="usuarioAtribuicaoOptions.length === 0"
          class="crm-empty compact"
        >
          Nenhum usuário ativo encontrado para atribuir ao CRM.
        </div>

        <div v-else class="crm-role-grid">
          <section
            v-for="papel in atribuicaoPapeis"
            :key="papel.chave"
            class="crm-role-section"
          >
            <div class="crm-role-header">
              <h4>{{ papel.nome }}</h4>
              <small>{{ papel.descricao }}</small>
            </div>
            <b-form-checkbox-group
              v-model="atribuicoesForm[papel.chave]"
              :options="usuarioAtribuicaoOptions"
              :disabled="saving"
              stacked
              class="crm-role-checks"
            />
          </section>
        </div>

        <div class="crm-modal-actions">
          <button
            class="btn btn-light"
            type="button"
            :disabled="saving"
            @click="modalAtribuicoes = false"
          >
            Fechar
          </button>
          <button
            class="btn btn-dark"
            type="button"
            :disabled="saving || loadingAtribuicoes"
            @click="salvarAtribuicoes"
          >
            Salvar atribuições
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      v-model="modalFunil"
      :title="funilForm.id_crm_funil ? 'Editar funil' : 'Novo funil'"
      hide-footer
    >
      <b-form @submit.prevent="salvarFunil">
        <b-form-group label="Nome">
          <b-form-input v-model="funilForm.nome" maxlength="100" autofocus />
        </b-form-group>
        <b-form-group label="Descrição">
          <b-form-textarea
            v-model="funilForm.descricao"
            rows="2"
            maxlength="255"
          />
        </b-form-group>
        <b-row>
          <b-col md="6">
            <b-form-group label="Ordem">
              <b-form-input
                v-model.number="funilForm.ordem"
                type="number"
                min="0"
              />
            </b-form-group>
          </b-col>
          <b-col md="6" class="d-flex align-items-center">
            <b-form-checkbox v-model="funilForm.padrao" class="mr-4">
              Funil padrão
            </b-form-checkbox>
            <b-form-checkbox v-model="funilForm.ativo"> Ativo </b-form-checkbox>
          </b-col>
        </b-row>
        <div class="d-flex justify-content-end">
          <button
            class="btn btn-light mr-2"
            type="button"
            @click="modalFunil = false"
          >
            Cancelar
          </button>
          <button class="btn btn-dark" type="submit" :disabled="saving">
            Salvar
          </button>
        </div>
      </b-form>
    </b-modal>

    <b-modal
      v-model="modalEtapa"
      :title="etapaForm.id_crm_etapa ? 'Editar etapa' : 'Nova etapa'"
      hide-footer
    >
      <b-form @submit.prevent="salvarEtapa">
        <b-form-group label="Funil">
          <b-form-select
            v-model="etapaForm.id_crm_funil"
            :options="funilOptions"
          />
        </b-form-group>
        <b-form-group label="Nome">
          <b-form-input v-model="etapaForm.nome" maxlength="80" autofocus />
        </b-form-group>
        <b-row>
          <b-col md="4">
            <b-form-group label="Ordem">
              <b-form-input
                v-model.number="etapaForm.ordem"
                type="number"
                min="0"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Probabilidade">
              <b-form-input
                v-model.number="etapaForm.probabilidade"
                type="number"
                min="0"
                max="100"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Cor">
              <b-form-input v-model="etapaForm.cor" type="color" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-checkbox v-model="etapaForm.ativo" class="mb-5">
          Ativa
        </b-form-checkbox>

        <div class="crm-automation-box mb-5">
          <h4>Automação simples</h4>
          <b-form-checkbox v-model="etapaForm.sugerir_tarefa" class="mb-3">
            Sugerir tarefa ao mover oportunidade para esta etapa
          </b-form-checkbox>
          <b-row v-if="etapaForm.sugerir_tarefa">
            <b-col md="6">
              <b-form-group label="Tipo da tarefa">
                <b-form-select
                  v-model="etapaForm.tarefa_sugerida_tipo"
                  :options="tipoTarefaOptions"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Prazo em horas">
                <b-form-input
                  v-model.number="etapaForm.tarefa_sugerida_prazo_horas"
                  type="number"
                  min="0"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-form-group v-if="etapaForm.sugerir_tarefa" label="Título sugerido">
            <b-form-input
              v-model="etapaForm.tarefa_sugerida_titulo"
              maxlength="140"
              placeholder="Ex.: Fazer follow-up com o cliente"
            />
          </b-form-group>

          <b-row>
            <b-col md="6">
              <b-form-checkbox v-model="etapaForm.exigir_motivo_perda">
                Exigir motivo ao perder nesta etapa
              </b-form-checkbox>
            </b-col>
            <b-col md="6">
              <b-form-checkbox v-model="etapaForm.destacar_sem_tarefa">
                Destacar oportunidade sem tarefa
              </b-form-checkbox>
            </b-col>
          </b-row>

          <b-row class="mt-3">
            <b-col md="6">
              <b-form-checkbox v-model="etapaForm.alertar_oportunidade_parada">
                Alertar oportunidade parada
              </b-form-checkbox>
            </b-col>
            <b-col md="6">
              <b-form-group label="Dias sem movimento">
                <b-form-input
                  v-model.number="etapaForm.alerta_parada_dias"
                  type="number"
                  min="1"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <div class="d-flex justify-content-end">
          <button
            class="btn btn-light mr-2"
            type="button"
            @click="modalEtapa = false"
          >
            Cancelar
          </button>
          <button class="btn btn-dark" type="submit" :disabled="saving">
            Salvar
          </button>
        </div>
      </b-form>
    </b-modal>

    <b-modal
      v-model="modalFonte"
      :title="fonteForm.id_crm_fonte ? 'Editar fonte' : 'Nova fonte'"
      hide-footer
    >
      <b-form @submit.prevent="salvarFonte">
        <b-form-group label="Nome">
          <b-form-input v-model="fonteForm.nome" maxlength="100" autofocus />
        </b-form-group>
        <b-form-checkbox v-model="fonteForm.ativo" class="mb-5">
          Ativa
        </b-form-checkbox>
        <div class="d-flex justify-content-end">
          <button
            class="btn btn-light mr-2"
            type="button"
            @click="modalFonte = false"
          >
            Cancelar
          </button>
          <button class="btn btn-dark" type="submit" :disabled="saving">
            Salvar
          </button>
        </div>
      </b-form>
    </b-modal>

    <b-modal
      v-model="modalMotivo"
      :title="motivoForm.id_crm_motivo_perda ? 'Editar motivo' : 'Novo motivo'"
      hide-footer
    >
      <b-form @submit.prevent="salvarMotivo">
        <b-form-group label="Nome">
          <b-form-input v-model="motivoForm.nome" maxlength="100" autofocus />
        </b-form-group>
        <b-form-checkbox v-model="motivoForm.ativo" class="mb-5">
          Ativo
        </b-form-checkbox>
        <div class="d-flex justify-content-end">
          <button
            class="btn btn-light mr-2"
            type="button"
            @click="modalMotivo = false"
          >
            Cancelar
          </button>
          <button class="btn btn-dark" type="submit" :disabled="saving">
            Salvar
          </button>
        </div>
      </b-form>
    </b-modal>

    <b-modal
      v-model="modalTipoTarefa"
      :title="
        tipoTarefaForm.id_crm_tipo_tarefa
          ? 'Editar tipo de tarefa'
          : 'Novo tipo de tarefa'
      "
      hide-footer
    >
      <b-form @submit.prevent="salvarTipoTarefa">
        <b-form-group label="Nome">
          <b-form-input
            v-model="tipoTarefaForm.nome"
            maxlength="100"
            autofocus
          />
        </b-form-group>
        <b-form-checkbox v-model="tipoTarefaForm.ativo" class="mb-5">
          Ativo
        </b-form-checkbox>
        <div class="d-flex justify-content-end">
          <button
            class="btn btn-light mr-2"
            type="button"
            @click="modalTipoTarefa = false"
          >
            Cancelar
          </button>
          <button class="btn btn-dark" type="submit" :disabled="saving">
            Salvar
          </button>
        </div>
      </b-form>
    </b-modal>

    <b-modal
      v-model="modalTemperatura"
      :title="
        temperaturaForm.id_crm_temperatura
          ? 'Editar temperatura'
          : 'Nova temperatura'
      "
      hide-footer
    >
      <b-form @submit.prevent="salvarTemperatura">
        <b-form-group label="Nome">
          <b-form-input
            v-model="temperaturaForm.nome"
            maxlength="80"
            autofocus
          />
        </b-form-group>
        <b-row>
          <b-col md="6">
            <b-form-group label="Cor">
              <b-form-input v-model="temperaturaForm.cor" type="color" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Peso">
              <b-form-input
                v-model.number="temperaturaForm.peso"
                type="number"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-checkbox v-model="temperaturaForm.ativo" class="mb-5">
          Ativa
        </b-form-checkbox>
        <div class="d-flex justify-content-end">
          <button
            class="btn btn-light mr-2"
            type="button"
            @click="modalTemperatura = false"
          >
            Cancelar
          </button>
          <button class="btn btn-dark" type="submit" :disabled="saving">
            Salvar
          </button>
        </div>
      </b-form>
    </b-modal>

    <b-modal
      v-model="modalCampoComercial"
      :title="
        campoComercialForm.id_crm_campo_comercial
          ? 'Editar campo comercial'
          : 'Novo campo comercial'
      "
      hide-footer
    >
      <b-form @submit.prevent="salvarCampoComercial">
        <b-form-group label="Nome">
          <b-form-input
            v-model="campoComercialForm.nome"
            maxlength="100"
            autofocus
          />
        </b-form-group>
        <b-row>
          <b-col md="6">
            <b-form-group label="Tipo">
              <b-form-select
                v-model="campoComercialForm.tipo"
                :options="tipoCampoOptions"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Ordem">
              <b-form-input
                v-model.number="campoComercialForm.ordem"
                type="number"
                min="0"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group v-if="campoComercialForm.tipo === 'lista'" label="Opções">
          <b-form-textarea
            v-model="campoComercialForm.opcoes"
            rows="3"
            placeholder="Uma opção por linha"
          />
        </b-form-group>
        <b-row>
          <b-col md="4">
            <b-form-checkbox v-model="campoComercialForm.obrigatorio">
              Obrigatório
            </b-form-checkbox>
          </b-col>
          <b-col md="4">
            <b-form-checkbox v-model="campoComercialForm.visivel_kanban">
              Kanban
            </b-form-checkbox>
          </b-col>
          <b-col md="4">
            <b-form-checkbox v-model="campoComercialForm.ativo">
              Ativo
            </b-form-checkbox>
          </b-col>
        </b-row>
        <div class="d-flex justify-content-end mt-5">
          <button
            class="btn btn-light mr-2"
            type="button"
            @click="modalCampoComercial = false"
          >
            Cancelar
          </button>
          <button class="btn btn-dark" type="submit" :disabled="saving">
            Salvar
          </button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import crmService from "@/modules/CRM/services/crmService";

const funilInicial = () => ({
  id_crm_funil: null,
  nome: "",
  descricao: "",
  padrao: false,
  ativo: true,
  ordem: 0,
});

const etapaInicial = () => ({
  id_crm_etapa: null,
  id_crm_funil: null,
  nome: "",
  ordem: 0,
  probabilidade: 0,
  cor: "#3699ff",
  ativo: true,
  sugerir_tarefa: false,
  tarefa_sugerida_tipo: "tarefa",
  tarefa_sugerida_titulo: "",
  tarefa_sugerida_prazo_horas: 24,
  exigir_motivo_perda: false,
  destacar_sem_tarefa: true,
  alertar_oportunidade_parada: true,
  alerta_parada_dias: 7,
});

const itemInicial = () => ({
  nome: "",
  ativo: true,
});

const temperaturaInicial = () => ({
  id_crm_temperatura: null,
  nome: "",
  cor: "#3699ff",
  peso: 0,
  ativo: true,
});

const campoComercialInicial = () => ({
  id_crm_campo_comercial: null,
  nome: "",
  tipo: "texto",
  opcoes: "",
  obrigatorio: false,
  visivel_kanban: false,
  ativo: true,
  ordem: 0,
});

const automacaoInicial = () => ({
  id_crm_automacao: null,
  nome: "",
  descricao: "",
  escopo: "crm",
  prioridade: 0,
  ativo: false,
  max_execucoes_por_evento: 1,
  gatilho: "oportunidade_criada",
  condicoes: [],
  acoes: [
    {
      acao: "criar_timeline",
      parametros: { descricao: "Automação executada no CRM." },
      parametros_texto: '{"descricao":"Automação executada no CRM."}',
    },
  ],
});

const atribuicoesInicial = () => ({
  gestor: [],
  vendedor: [],
  desenvolvedor: [],
  website: [],
  treinamento: [],
});

const atribuicaoPapeis = [
  {
    chave: "gestor",
    nome: "Gestores",
    descricao: "Acompanham todos os cards e recebem os contratos.",
  },
  {
    chave: "vendedor",
    nome: "Vendedores",
    descricao: "Criam oportunidades e conduzem vendas e pós-venda.",
  },
  {
    chave: "desenvolvedor",
    nome: "Desenvolvedores",
    descricao: "Recebem apresentação técnica e implementação white label.",
  },
  {
    chave: "website",
    nome: "Website",
    descricao: "Recebem somente os cards do funil de website.",
  },
  {
    chave: "treinamento",
    nome: "Treinamento",
    descricao: "Recebem somente os cards do funil de treinamento.",
  },
];

export default {
  name: "crm-configuracoes",
  data() {
    return {
      loading: false,
      loadingAtribuicoes: false,
      loadingPreConfiguracoes: false,
      saving: false,
      selectedFunilId: null,
      acessosFunilForm: [],
      atribuicaoPapeis,
      atribuicoesForm: atribuicoesInicial(),
      usuariosAtribuicoes: [],
      preConfiguracoes: [],
      funis: [],
      fontes: [],
      motivos: [],
      tiposTarefa: [],
      temperaturas: [],
      camposComerciais: [],
      usuarios: [],
      automacoes: [],
      automacaoOpcoes: {
        gatilhos: [],
        condicoes: [],
        acoes: [],
        operadores: [],
        usuarios: [],
        funis: [],
        etapas: [],
        campos_comerciais: [],
      },
      automacaoExecucoes: [],
      automacaoSimulacao: {
        id_crm_oportunidade: null,
        resultado: null,
      },
      modalFunil: false,
      modalPreConfiguracoes: false,
      modalAtribuicoes: false,
      modalEtapa: false,
      modalFonte: false,
      modalMotivo: false,
      modalTipoTarefa: false,
      modalTemperatura: false,
      modalCampoComercial: false,
      funilForm: funilInicial(),
      etapaForm: etapaInicial(),
      fonteForm: itemInicial(),
      motivoForm: itemInicial(),
      tipoTarefaForm: itemInicial(),
      temperaturaForm: temperaturaInicial(),
      campoComercialForm: campoComercialInicial(),
      automacaoForm: automacaoInicial(),
    };
  },
  computed: {
    funisOrdenados() {
      return [...this.funis].sort((a, b) => {
        const ordemA = Number(a.ordem || 0);
        const ordemB = Number(b.ordem || 0);
        if (ordemA !== ordemB) {
          return ordemA - ordemB;
        }
        return String(a.nome || "").localeCompare(String(b.nome || ""));
      });
    },
    funilSelecionado() {
      return this.funisOrdenados.find(
        (funil) => funil.id_crm_funil === this.selectedFunilId
      );
    },
    etapasSelecionadas() {
      const etapas =
        this.funilSelecionado && this.funilSelecionado.etapas
          ? this.funilSelecionado.etapas
          : [];

      return [...etapas].sort(
        (a, b) => Number(a.ordem || 0) - Number(b.ordem || 0)
      );
    },
    funilOptions() {
      return this.funisOrdenados.map((funil) => ({
        value: funil.id_crm_funil,
        text: funil.nome,
      }));
    },
    usuarioOptionsSemTodos() {
      return this.usuarios.map((usuario) => ({
        value: usuario.id_usuario,
        text: usuario.nome || usuario.usuario,
      }));
    },
    usuarioAtribuicaoOptions() {
      const usuarios = this.usuariosAtribuicoes.length
        ? this.usuariosAtribuicoes
        : this.usuarios;

      return usuarios.map((usuario) => ({
        value: usuario.id_usuario,
        text: usuario.nome || usuario.usuario,
      }));
    },
    tipoTarefaOptions() {
      const ativos = this.tiposTarefa.filter((tipo) => tipo.ativo !== false);
      const tipos = ativos.length
        ? ativos
        : [{ nome: "Tarefa", chave: "tarefa" }];

      return tipos.map((tipo) => ({
        value: tipo.chave || this.slug(tipo.nome),
        text: tipo.nome,
      }));
    },
    tipoCampoOptions() {
      return [
        { value: "texto", text: "Texto" },
        { value: "numero", text: "Número" },
        { value: "data", text: "Data" },
        { value: "lista", text: "Lista" },
        { value: "booleano", text: "Sim/Não" },
      ];
    },
    automacaoGatilhoOptions() {
      return this.opcoesAutomacaoSelect("gatilhos");
    },
    automacaoCondicaoOptions() {
      return this.opcoesAutomacaoSelect("condicoes");
    },
    automacaoAcaoOptions() {
      return this.opcoesAutomacaoSelect("acoes");
    },
    automacaoOperadorOptions() {
      return this.opcoesAutomacaoSelect("operadores");
    },
    automacaoUsuarioOptions() {
      const usuariosAutomacao = this.automacaoOpcoes.usuarios || [];
      const usuarios = usuariosAutomacao.length
        ? usuariosAutomacao
        : this.usuarios;

      return usuarios.map((usuario) => ({
        value: usuario.id_usuario,
        text: usuario.nome || usuario.usuario,
      }));
    },
    automacaoUsuarioOptionsComVazio() {
      return [
        { value: null, text: "Manter atual / não definido" },
        ...this.automacaoUsuarioOptions,
      ];
    },
    automacaoFunilOptions() {
      const funisAutomacao = this.automacaoOpcoes.funis || [];
      const funis = funisAutomacao.length
        ? funisAutomacao
        : this.funisOrdenados;

      return funis.map((funil) => ({
        value: funil.id_crm_funil,
        text: funil.nome,
      }));
    },
    automacaoFunilOptionsComVazio() {
      return [
        { value: null, text: "Selecione um funil" },
        ...this.automacaoFunilOptions,
      ];
    },
    automacaoCampoOptions() {
      const camposAutomacao = this.automacaoOpcoes.campos_comerciais || [];
      const campos = camposAutomacao.length
        ? camposAutomacao
        : this.camposComerciais;

      return campos.map((campo) => ({
        value: campo.id_crm_campo_comercial,
        text: campo.nome,
        chave: campo.chave,
      }));
    },
    automacaoCampoOptionsComVazio() {
      return [
        { value: null, text: "Selecione um campo" },
        ...this.automacaoCampoOptions,
      ];
    },
  },
  mounted() {
    this.loadAll();
  },
  watch: {
    selectedFunilId() {
      this.syncAcessosFunilForm();
    },
  },
  methods: {
    async loadAll() {
      this.loading = true;

      try {
        const responses = await Promise.allSettled([
          crmService.funis({
            per_page: 100,
            incluir_etapas_inativas: true,
          }),
          crmService.fontesConfiguracao(),
          crmService.motivosPerdaConfiguracao(),
          crmService.tiposTarefaConfiguracao(),
          crmService.temperaturasConfiguracao(),
          crmService.camposComerciaisConfiguracao(),
          crmService.preConfiguracoesConfiguracao(),
          crmService.usuarios({
            per_page: 100,
            page: 1,
            sort: [{ field: "nome", type: "asc" }],
          }),
          crmService.automacoesOpcoes(),
          crmService.automacoes({ per_page: 100 }),
          crmService.automacaoExecucoes({ per_page: 8 }),
        ]);
        const [
          funis,
          fontes,
          motivos,
          tiposTarefa,
          temperaturas,
          camposComerciais,
          preConfiguracoes,
          usuarios,
          automacaoOpcoes,
          automacoes,
          automacaoExecucoes,
        ] = responses.map((response) =>
          response.status === "fulfilled" ? response.value : null
        );

        this.funis = this.normalizarColecao(funis);
        this.fontes = this.normalizarColecao(fontes);
        this.motivos = this.normalizarColecao(motivos);
        this.tiposTarefa = this.normalizarColecao(tiposTarefa);
        this.temperaturas = this.normalizarColecao(temperaturas);
        this.camposComerciais = this.normalizarColecao(camposComerciais);
        this.preConfiguracoes = this.normalizarColecao(preConfiguracoes);
        this.usuarios = this.normalizarColecao(usuarios);
        this.automacaoOpcoes = this.normalizarObjeto(
          automacaoOpcoes,
          this.automacaoOpcoes
        );
        this.automacoes = this.normalizarColecao(automacoes);
        this.automacaoExecucoes = this.normalizarColecao(automacaoExecucoes);
        this.manterFunilSelecionado();
        this.syncAcessosFunilForm();

        const falhas = responses.filter(
          (response) => response.status === "rejected"
        );
        if (falhas.length) {
          this.$toast.error(
            this.mensagemErro(
              falhas[0].reason,
              "Algumas configurações não puderam ser carregadas."
            )
          );
        }
      } catch (error) {
        this.$toast.error(
          this.mensagemErro(
            error,
            "Não foi possível carregar as configurações."
          )
        );
      } finally {
        this.loading = false;
      }
    },
    normalizarColecao(response) {
      const body =
        response && response.data !== undefined ? response.data : response;
      const data = body && body.data !== undefined ? body.data : body;

      if (Array.isArray(data)) {
        return data;
      }

      if (data && Array.isArray(data.data)) {
        return data.data;
      }

      return [];
    },
    normalizarObjeto(response, fallback = {}) {
      const body =
        response && response.data !== undefined ? response.data : response;
      const data = body && body.data !== undefined ? body.data : body;

      return data && typeof data === "object" && !Array.isArray(data)
        ? data
        : fallback;
    },
    manterFunilSelecionado() {
      if (
        this.selectedFunilId &&
        this.funis.some((funil) => funil.id_crm_funil === this.selectedFunilId)
      ) {
        return;
      }

      this.selectedFunilId = this.funisOrdenados.length
        ? this.funisOrdenados[0].id_crm_funil
        : null;
    },
    syncAcessosFunilForm() {
      const acessos =
        this.funilSelecionado && this.funilSelecionado.usuarios_acesso
          ? this.funilSelecionado.usuarios_acesso
          : [];

      this.acessosFunilForm = acessos
        .filter((acesso) => acesso.ativo !== false)
        .map((acesso) => Number(acesso.id_usuario));
    },
    abrirModalAtribuicoes() {
      this.modalAtribuicoes = true;
      this.loadAtribuicoes();
    },
    async loadAtribuicoes() {
      this.loadingAtribuicoes = true;

      try {
        const responses = await Promise.allSettled([
          crmService.atribuicoesConfiguracao(),
          crmService.usuariosAtribuicoesConfiguracao(),
        ]);
        const [atribuicoesResult, usuariosResult] = responses;
        const atribuicoesResponse =
          atribuicoesResult.status === "fulfilled"
            ? atribuicoesResult.value
            : null;
        const usuariosResponse =
          usuariosResult.status === "fulfilled" ? usuariosResult.value : null;
        const atribuicoesData = this.normalizarObjeto(atribuicoesResponse, {});
        const usuariosData = this.normalizarObjeto(usuariosResponse, {});

        this.usuariosAtribuicoes = Array.isArray(usuariosData.usuarios)
          ? usuariosData.usuarios
          : this.normalizarColecao(usuariosResponse);
        this.atribuicoesFromResponse(atribuicoesData);

        const falha = responses.find(
          (response) => response.status === "rejected"
        );
        if (falha) {
          this.$toast.error(
            this.mensagemErro(
              falha.reason,
              "Algumas informações de atribuições não puderam ser carregadas."
            )
          );
        }
      } catch (error) {
        this.$toast.error(
          this.mensagemErro(error, "Não foi possível carregar as atribuições.")
        );
      } finally {
        this.loadingAtribuicoes = false;
      }
    },
    atribuicoesFromResponse(data) {
      const origem = data && data.atribuicoes ? data.atribuicoes : {};
      const form = atribuicoesInicial();

      this.atribuicaoPapeis.forEach((papel) => {
        const itens = Array.isArray(origem[papel.chave])
          ? origem[papel.chave]
          : [];

        form[papel.chave] = itens
          .map((item) => Number(item.id_usuario || item))
          .filter(Boolean);
      });

      this.atribuicoesForm = form;
    },
    async salvarAtribuicoes() {
      this.saving = true;

      try {
        const payload = {
          atribuicoes: {},
        };

        this.atribuicaoPapeis.forEach((papel) => {
          payload.atribuicoes[papel.chave] = [
            ...new Set(
              (this.atribuicoesForm[papel.chave] || [])
                .map((idUsuario) => Number(idUsuario))
                .filter(Boolean)
            ),
          ];
        });

        const response = await crmService.salvarAtribuicoesConfiguracao(
          payload
        );
        const body = response && response.data ? response.data : response;

        if (body && body.status === false) {
          this.$toast.error(
            body.msg || "Não foi possível salvar as atribuições."
          );
          return;
        }

        this.atribuicoesFromResponse(body && body.data ? body.data : body);
        this.$toast.success("Atribuições salvas com sucesso.");
      } catch (error) {
        this.$toast.error(
          this.mensagemErro(error, "Não foi possível salvar as atribuições.")
        );
      } finally {
        this.saving = false;
      }
    },
    async salvarAcessosFunil() {
      if (!this.funilSelecionado) {
        this.$toast.error("Selecione um funil.");
        return;
      }

      const salvo = await this.executar(
        crmService.atualizarAcessosFunil({
          id_crm_funil: this.funilSelecionado.id_crm_funil,
          usuarios: this.acessosFunilForm,
        }),
        "Acessos do funil salvos com sucesso."
      );

      if (salvo && salvo.id_crm_funil) {
        this.selectedFunilId = salvo.id_crm_funil;
        this.syncAcessosFunilForm();
      }
    },
    proximaOrdemFunil() {
      return (
        this.funis.reduce(
          (maior, funil) => Math.max(maior, Number(funil.ordem || 0)),
          0
        ) + 1
      );
    },
    proximaOrdemEtapa(funil) {
      const etapas = funil && funil.etapas ? funil.etapas : [];
      return (
        etapas.reduce(
          (maior, etapa) => Math.max(maior, Number(etapa.ordem || 0)),
          0
        ) + 1
      );
    },
    novoFunil() {
      this.funilForm = {
        ...funilInicial(),
        ordem: this.proximaOrdemFunil(),
        padrao: this.funis.length === 0,
      };
      this.modalFunil = true;
    },
    editarFunil(funil) {
      this.funilForm = {
        id_crm_funil: funil.id_crm_funil,
        nome: funil.nome || "",
        descricao: funil.descricao || "",
        padrao: Boolean(funil.padrao),
        ativo: funil.ativo !== false,
        ordem: Number(funil.ordem || 0),
      };
      this.modalFunil = true;
    },
    async salvarFunil() {
      if (!this.funilForm.nome) {
        this.$toast.error("Informe o nome do funil.");
        return;
      }

      const payload = {
        ...this.funilForm,
        ordem: Number(this.funilForm.ordem || 0),
      };

      const request = payload.id_crm_funil
        ? crmService.atualizarFunil(payload)
        : crmService.criarFunil(payload);

      const salvo = await this.executar(request, "Funil salvo com sucesso.");
      if (salvo) {
        this.modalFunil = false;
        if (salvo.id_crm_funil) {
          this.selectedFunilId = salvo.id_crm_funil;
        }
      }
    },
    async apagarFunil(funil) {
      if (!funil || !funil.id_crm_funil) {
        return;
      }

      const confirmado = this.$bvModal
        ? await this.$bvModal.msgBoxConfirm(
            `Apagar o funil "${funil.nome}"? Esta ação não será permitida se houver cards abertos nesse funil.`,
            {
              title: "Apagar funil",
              okTitle: "Apagar",
              cancelTitle: "Cancelar",
              okVariant: "danger",
              cancelVariant: "light",
            }
          )
        : window.confirm(`Apagar o funil "${funil.nome}"?`);

      if (!confirmado) {
        return;
      }

      const salvo = await this.executar(
        crmService.apagarFunil({ id_crm_funil: funil.id_crm_funil }),
        "Funil apagado com sucesso."
      );

      if (salvo) {
        this.selectedFunilId = null;
        this.manterFunilSelecionado();
      }
    },
    novaEtapa(funil = this.funilSelecionado) {
      this.etapaForm = {
        ...etapaInicial(),
        id_crm_funil: funil ? funil.id_crm_funil : null,
        ordem: this.proximaOrdemEtapa(funil),
      };
      this.modalEtapa = true;
    },
    editarEtapa(etapa) {
      this.etapaForm = {
        id_crm_etapa: etapa.id_crm_etapa,
        id_crm_funil: etapa.id_crm_funil,
        nome: etapa.nome || "",
        ordem: Number(etapa.ordem || 0),
        probabilidade: Number(etapa.probabilidade || 0),
        cor: etapa.cor || "#3699ff",
        ativo: etapa.ativo !== false,
        sugerir_tarefa: Boolean(etapa.sugerir_tarefa),
        tarefa_sugerida_tipo: etapa.tarefa_sugerida_tipo || "tarefa",
        tarefa_sugerida_titulo: etapa.tarefa_sugerida_titulo || "",
        tarefa_sugerida_prazo_horas: Number(
          etapa.tarefa_sugerida_prazo_horas || 24
        ),
        exigir_motivo_perda: Boolean(etapa.exigir_motivo_perda),
        destacar_sem_tarefa: etapa.destacar_sem_tarefa !== false,
        alertar_oportunidade_parada:
          etapa.alertar_oportunidade_parada !== false,
        alerta_parada_dias: Number(etapa.alerta_parada_dias || 7),
      };
      this.modalEtapa = true;
    },
    async salvarEtapa() {
      if (!this.etapaForm.id_crm_funil) {
        this.$toast.error("Informe o funil.");
        return;
      }

      if (!this.etapaForm.nome) {
        this.$toast.error("Informe o nome da etapa.");
        return;
      }

      const payload = {
        ...this.etapaForm,
        ordem: Number(this.etapaForm.ordem || 0),
        probabilidade: Number(this.etapaForm.probabilidade || 0),
        tarefa_sugerida_prazo_horas:
          Number(this.etapaForm.tarefa_sugerida_prazo_horas || 0) || null,
        alerta_parada_dias: Number(this.etapaForm.alerta_parada_dias || 7),
      };

      const request = payload.id_crm_etapa
        ? crmService.atualizarEtapa(payload)
        : crmService.criarEtapa(payload);

      const salvo = await this.executar(request, "Etapa salva com sucesso.");
      if (salvo) {
        this.modalEtapa = false;
        this.selectedFunilId = payload.id_crm_funil;
      }
    },
    novaFonte() {
      this.fonteForm = itemInicial();
      this.modalFonte = true;
    },
    editarFonte(fonte) {
      this.fonteForm = {
        id_crm_fonte: fonte.id_crm_fonte,
        nome: fonte.nome || "",
        ativo: fonte.ativo !== false,
      };
      this.modalFonte = true;
    },
    async salvarFonte() {
      if (!this.fonteForm.nome) {
        this.$toast.error("Informe o nome da fonte.");
        return;
      }

      const request = this.fonteForm.id_crm_fonte
        ? crmService.atualizarFonte(this.fonteForm)
        : crmService.criarFonte(this.fonteForm);

      const salvo = await this.executar(request, "Fonte salva com sucesso.");
      if (salvo) {
        this.modalFonte = false;
      }
    },
    novoMotivo() {
      this.motivoForm = itemInicial();
      this.modalMotivo = true;
    },
    editarMotivo(motivo) {
      this.motivoForm = {
        id_crm_motivo_perda: motivo.id_crm_motivo_perda,
        nome: motivo.nome || "",
        ativo: motivo.ativo !== false,
      };
      this.modalMotivo = true;
    },
    async salvarMotivo() {
      if (!this.motivoForm.nome) {
        this.$toast.error("Informe o motivo de perda.");
        return;
      }

      const request = this.motivoForm.id_crm_motivo_perda
        ? crmService.atualizarMotivoPerda(this.motivoForm)
        : crmService.criarMotivoPerda(this.motivoForm);

      const salvo = await this.executar(request, "Motivo salvo com sucesso.");
      if (salvo) {
        this.modalMotivo = false;
      }
    },
    novoTipoTarefa() {
      this.tipoTarefaForm = itemInicial();
      this.modalTipoTarefa = true;
    },
    editarTipoTarefa(tipo) {
      this.tipoTarefaForm = {
        id_crm_tipo_tarefa: tipo.id_crm_tipo_tarefa,
        nome: tipo.nome || "",
        ativo: tipo.ativo !== false,
      };
      this.modalTipoTarefa = true;
    },
    async salvarTipoTarefa() {
      if (!this.tipoTarefaForm.nome) {
        this.$toast.error("Informe o tipo de tarefa.");
        return;
      }

      const request = this.tipoTarefaForm.id_crm_tipo_tarefa
        ? crmService.atualizarTipoTarefa(this.tipoTarefaForm)
        : crmService.criarTipoTarefa(this.tipoTarefaForm);

      const salvo = await this.executar(
        request,
        "Tipo de tarefa salvo com sucesso."
      );
      if (salvo) {
        this.modalTipoTarefa = false;
      }
    },
    novaTemperatura() {
      this.temperaturaForm = {
        ...temperaturaInicial(),
        peso: this.proximaOrdemTemperatura(),
      };
      this.modalTemperatura = true;
    },
    editarTemperatura(temperatura) {
      this.temperaturaForm = {
        id_crm_temperatura: temperatura.id_crm_temperatura,
        nome: temperatura.nome || "",
        cor: temperatura.cor || "#3699ff",
        peso: Number(temperatura.peso || 0),
        ativo: temperatura.ativo !== false,
      };
      this.modalTemperatura = true;
    },
    async salvarTemperatura() {
      if (!this.temperaturaForm.nome) {
        this.$toast.error("Informe a temperatura.");
        return;
      }

      const payload = {
        ...this.temperaturaForm,
        peso: Number(this.temperaturaForm.peso || 0),
      };
      const request = payload.id_crm_temperatura
        ? crmService.atualizarTemperatura(payload)
        : crmService.criarTemperatura(payload);

      const salvo = await this.executar(
        request,
        "Temperatura salva com sucesso."
      );
      if (salvo) {
        this.modalTemperatura = false;
      }
    },
    novoCampoComercial() {
      this.campoComercialForm = {
        ...campoComercialInicial(),
        ordem: this.proximaOrdemCampoComercial(),
      };
      this.modalCampoComercial = true;
    },
    editarCampoComercial(campo) {
      this.campoComercialForm = {
        id_crm_campo_comercial: campo.id_crm_campo_comercial,
        nome: campo.nome || "",
        tipo: campo.tipo || "texto",
        opcoes: campo.opcoes || "",
        obrigatorio: Boolean(campo.obrigatorio),
        visivel_kanban: Boolean(campo.visivel_kanban),
        ativo: campo.ativo !== false,
        ordem: Number(campo.ordem || 0),
      };
      this.modalCampoComercial = true;
    },
    async salvarCampoComercial() {
      if (!this.campoComercialForm.nome) {
        this.$toast.error("Informe o nome do campo comercial.");
        return;
      }

      const payload = {
        ...this.campoComercialForm,
        ordem: Number(this.campoComercialForm.ordem || 0),
      };
      const request = payload.id_crm_campo_comercial
        ? crmService.atualizarCampoComercial(payload)
        : crmService.criarCampoComercial(payload);

      const salvo = await this.executar(
        request,
        "Campo comercial salvo com sucesso."
      );
      if (salvo) {
        this.modalCampoComercial = false;
      }
    },
    abrirModalPreConfiguracoes() {
      this.modalPreConfiguracoes = true;
      this.loadPreConfiguracoes();
    },
    async loadPreConfiguracoes() {
      this.loadingPreConfiguracoes = true;

      try {
        const response = await crmService.preConfiguracoesConfiguracao();
        this.preConfiguracoes = this.normalizarColecao(response);
      } catch (error) {
        this.$toast.error(
          this.mensagemErro(
            error,
            "Não foi possível carregar as pré-configurações."
          )
        );
      } finally {
        this.loadingPreConfiguracoes = false;
      }
    },
    async instalarPreConfiguracao(preset) {
      if (!preset || preset.status_instalacao === "instalado") {
        return;
      }

      const salvo = await this.executar(
        crmService.instalarPresetConfiguracao({ preset: preset.chave }),
        `${preset.nome} instalada/atualizada com sucesso.`
      );

      if (salvo) {
        this.$toast.success(
          `${salvo.funis || 0} funis, ${salvo.etapas || 0} etapas e ${
            salvo.campos || 0
          } campos prontos. ${salvo.automacoes || 0} automações configuradas.`
        );
        await Promise.all([this.loadAll(), this.loadPreConfiguracoes()]);
      }
    },
    labelStatusPreConfiguracao(preset) {
      const status = preset?.status_instalacao || "nao_instalado";

      if (status === "instalado") {
        return "Instalado";
      }

      if (status === "incompleto") {
        return "Incompleto";
      }

      return "Não instalado";
    },
    variantStatusPreConfiguracao(preset) {
      const status = preset?.status_instalacao || "nao_instalado";

      if (status === "instalado") {
        return "success";
      }

      if (status === "incompleto") {
        return "warning";
      }

      return "secondary";
    },
    labelAcaoPreConfiguracao(preset) {
      const status = preset?.status_instalacao || "nao_instalado";

      if (status === "instalado") {
        return "Instalado";
      }

      if (status === "incompleto") {
        return "Atualizar";
      }

      return "Instalar";
    },
    novaAutomacao() {
      this.automacaoForm = automacaoInicial();
      this.automacaoSimulacao.resultado = null;
    },
    editarAutomacao(automacao) {
      const gatilho =
        automacao.gatilhos && automacao.gatilhos.length
          ? automacao.gatilhos[0].gatilho
          : "oportunidade_criada";

      this.automacaoForm = {
        id_crm_automacao: automacao.id_crm_automacao,
        nome: automacao.nome || "",
        descricao: automacao.descricao || "",
        escopo: automacao.escopo || "crm",
        prioridade: Number(automacao.prioridade || 0),
        ativo: Boolean(automacao.ativo),
        max_execucoes_por_evento: Number(
          automacao.max_execucoes_por_evento || 1
        ),
        gatilho,
        condicoes: (automacao.condicoes || []).map((condicao) => ({
          campo: condicao.campo || "status",
          operador: condicao.operador || "igual",
          valor: this.valorCondicaoTexto(condicao.valor),
        })),
        acoes: (automacao.acoes && automacao.acoes.length
          ? automacao.acoes
          : [{ acao: "criar_timeline", parametros: null }]
        ).map((acao) => this.acaoAutomacaoForm(acao)),
      };
      this.automacaoSimulacao.resultado = null;
    },
    acaoAutomacaoForm(acao) {
      const parametros = this.prepararParametrosAcao(
        acao.acao || "criar_timeline",
        acao.parametros
      );

      return {
        acao: acao.acao || "criar_timeline",
        parametros,
        parametros_texto: this.jsonTexto(parametros),
      };
    },
    adicionarCondicao() {
      this.automacaoForm.condicoes.push({
        campo: "status",
        operador: "igual",
        valor: "",
      });
    },
    removerCondicao(index) {
      this.automacaoForm.condicoes.splice(index, 1);
    },
    adicionarAcao() {
      this.automacaoForm.acoes.push(
        this.acaoAutomacaoForm({
          acao: "criar_timeline",
          parametros: { descricao: "Automação executada no CRM." },
        })
      );
    },
    removerAcao(index) {
      if (this.automacaoForm.acoes.length === 1) {
        return;
      }

      this.automacaoForm.acoes.splice(index, 1);
    },
    async salvarAutomacao() {
      if (!this.automacaoForm.nome) {
        this.$toast.error("Informe o nome da automação.");
        return;
      }

      if (!this.automacaoForm.gatilho) {
        this.$toast.error("Selecione um gatilho.");
        return;
      }

      const payload = this.automacaoPayload();
      if (!payload) {
        return;
      }

      const request = payload.id_crm_automacao
        ? crmService.atualizarAutomacao(payload)
        : crmService.criarAutomacao(payload);

      const salvo = await this.executar(
        request,
        "Automação salva com sucesso."
      );
      if (salvo) {
        this.editarAutomacao(salvo);
      }
    },
    async alternarStatusAutomacao(automacao) {
      const ativo = !automacao.ativo;
      const salvo = await this.executar(
        crmService.alterarStatusAutomacao({
          id_crm_automacao: automacao.id_crm_automacao,
          ativo,
        }),
        ativo
          ? "Automação ativada com sucesso."
          : "Automação inativada com sucesso."
      );

      if (
        salvo &&
        this.automacaoForm.id_crm_automacao === automacao.id_crm_automacao
      ) {
        this.editarAutomacao(salvo);
      }
    },
    async excluirAutomacao(automacao) {
      const confirmado = this.$bvModal
        ? await this.$bvModal.msgBoxConfirm(
            `Excluir a automação "${automacao.nome}"? O histórico de execuções será preservado.`,
            {
              title: "Excluir automação",
              okTitle: "Excluir",
              cancelTitle: "Cancelar",
              okVariant: "danger",
              cancelVariant: "light",
            }
          )
        : window.confirm(`Excluir a automação "${automacao.nome}"?`);

      if (!confirmado) {
        return;
      }

      const removida = await this.executar(
        crmService.apagarAutomacao({
          id_crm_automacao: automacao.id_crm_automacao,
        }),
        "Automação excluída com sucesso."
      );

      if (
        removida &&
        this.automacaoForm.id_crm_automacao === automacao.id_crm_automacao
      ) {
        this.novaAutomacao();
      }
    },
    automacaoPayload() {
      const acoes = [];

      for (const acao of this.automacaoForm.acoes) {
        const parametros = this.sincronizarParametrosTexto(acao);
        if (!parametros) {
          return null;
        }

        acoes.push({
          acao: acao.acao,
          parametros,
          ativo: true,
        });
      }

      return {
        id_crm_automacao: this.automacaoForm.id_crm_automacao,
        nome: this.automacaoForm.nome,
        descricao: this.automacaoForm.descricao,
        escopo: this.automacaoForm.escopo || "crm",
        prioridade: Number(this.automacaoForm.prioridade || 0),
        ativo: Boolean(this.automacaoForm.ativo),
        max_execucoes_por_evento: Number(
          this.automacaoForm.max_execucoes_por_evento || 1
        ),
        gatilhos: [
          {
            gatilho: this.automacaoForm.gatilho,
            ativo: true,
            ordem: 1,
          },
        ],
        condicoes: this.automacaoForm.condicoes
          .filter((condicao) => condicao.campo)
          .map((condicao, index) => ({
            campo: condicao.campo,
            operador: condicao.operador || "igual",
            valor: condicao.valor,
            ativo: true,
            ordem: index + 1,
          })),
        acoes,
      };
    },
    prepararParametrosAcao(tipoAcao, parametrosOriginais) {
      const parametros =
        parametrosOriginais &&
        typeof parametrosOriginais === "object" &&
        !Array.isArray(parametrosOriginais)
          ? { ...parametrosOriginais }
          : {};

      if (tipoAcao === "criar_solicitacao_agenda_tecnica") {
        parametros.tipo_tarefa =
          parametros.tipo_tarefa || "apresentacao_tecnica";
        parametros.titulo = parametros.titulo || "Apresentação técnica";
        parametros.conceder_acesso_funil_tecnico =
          parametros.conceder_acesso_funil_tecnico !== false;
      }

      if (tipoAcao === "criar_tarefa") {
        parametros.titulo = parametros.titulo || "";
        parametros.tipo_tarefa = parametros.tipo_tarefa || "tarefa";
      }

      if (tipoAcao === "exigir_campo_antes_mover") {
        parametros.mensagem = parametros.mensagem || "";
        this.sincronizarCampoObrigatorioAcao({ parametros }, false);
      }

      if (tipoAcao === "atualizar_cliente") {
        parametros.campos_obrigatorios_texto = Array.isArray(
          parametros.campos_obrigatorios
        )
          ? parametros.campos_obrigatorios.join(", ")
          : parametros.campos_obrigatorios_texto || "";
      }

      if (tipoAcao === "enviar_link_agenda") {
        parametros.tipo_tarefa = parametros.tipo_tarefa || "tarefa";
        parametros.obrigatorio_para_concluir = Boolean(
          parametros.obrigatorio_para_concluir
        );
      }

      return parametros;
    },
    aoAlterarAcao(acao) {
      this.$set(
        acao,
        "parametros",
        this.prepararParametrosAcao(acao.acao, acao.parametros)
      );
      this.atualizarParametrosTexto(acao);
    },
    acaoTemDestino(acao) {
      return ["mover_funil", "criar_card_funil", "mover_etapa"].includes(
        acao.acao
      );
    },
    acaoTemAtribuicao(acao) {
      return [
        "criar_solicitacao_agenda_tecnica",
        "mover_funil",
        "criar_card_funil",
      ].includes(acao.acao);
    },
    labelAtribuicaoAcao(acao) {
      return acao.acao === "criar_solicitacao_agenda_tecnica"
        ? "Técnico da apresentação"
        : "Responsável pelo card";
    },
    acaoTemLiberacaoFunil(acao) {
      return [
        "criar_solicitacao_agenda_tecnica",
        "mover_funil",
        "criar_card_funil",
      ].includes(acao.acao);
    },
    automacaoEtapaOptionsPorFunil(idFunil) {
      const etapasAutomacao = this.automacaoOpcoes.etapas || [];
      const etapas = etapasAutomacao.length
        ? etapasAutomacao
        : this.funisOrdenados.flatMap((funil) => funil.etapas || []);
      const funil = idFunil ? Number(idFunil) : null;
      const filtradas = funil
        ? etapas.filter((etapa) => Number(etapa.id_crm_funil) === funil)
        : etapas;

      return [
        { value: null, text: "Selecione uma etapa" },
        ...filtradas.map((etapa) => ({
          value: etapa.id_crm_etapa,
          text: etapa.nome,
        })),
      ];
    },
    sincronizarCampoObrigatorioAcao(acao, atualizarTexto = true) {
      const parametros = acao.parametros || {};
      const idCampo = parametros.id_crm_campo_comercial;
      const camposAutomacao = this.automacaoOpcoes.campos_comerciais || [];
      const campos = camposAutomacao.length
        ? camposAutomacao
        : this.camposComerciais;
      const campo = campos.find(
        (item) => Number(item.id_crm_campo_comercial) === Number(idCampo)
      );

      if (campo) {
        this.$set(parametros, "campo", campo.chave);
      }

      if (atualizarTexto) {
        this.atualizarParametrosTexto(acao);
      }
    },
    atualizarCamposObrigatoriosAcao(acao) {
      const texto = acao.parametros.campos_obrigatorios_texto || "";
      this.$set(
        acao.parametros,
        "campos_obrigatorios",
        texto
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      );
      this.atualizarParametrosTexto(acao);
    },
    atualizarParametrosTexto(acao) {
      const parametros = this.normalizarParametrosAcao(acao);
      this.$set(acao, "parametros_texto", this.jsonTexto(parametros));

      return parametros;
    },
    sincronizarParametrosTexto(acao, usarTexto = false) {
      const parametrosTexto = this.parseJsonSeguro(acao.parametros_texto);
      if (parametrosTexto === false) {
        this.$toast.error("Revise o JSON dos parâmetros da ação.");
        return null;
      }

      const origem = usarTexto
        ? parametrosTexto
        : { ...parametrosTexto, ...(acao.parametros || {}) };
      const preparados = this.prepararParametrosAcao(acao.acao, origem);
      this.$set(acao, "parametros", preparados);

      return this.atualizarParametrosTexto(acao);
    },
    normalizarParametrosAcao(acao) {
      const parametros = { ...(acao.parametros || {}) };
      const numberKeys = [
        "id_usuario",
        "id_usuario_responsavel",
        "id_usuario_tecnico",
        "id_crm_funil_destino",
        "id_crm_etapa_destino",
        "id_crm_funil_tecnico",
        "id_crm_etapa_tecnico",
        "id_crm_campo_comercial",
      ];

      numberKeys.forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(parametros, key)) {
          return;
        }

        parametros[key] =
          parametros[key] === "" || parametros[key] === null
            ? null
            : Number(parametros[key]);
      });

      ["conceder_acesso_funil_tecnico", "obrigatorio_para_concluir"].forEach(
        (key) => {
          if (Object.prototype.hasOwnProperty.call(parametros, key)) {
            parametros[key] = Boolean(parametros[key]);
          }
        }
      );

      if (
        Object.prototype.hasOwnProperty.call(
          parametros,
          "campos_obrigatorios_texto"
        )
      ) {
        parametros.campos_obrigatorios = String(
          parametros.campos_obrigatorios_texto || ""
        )
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
        delete parametros.campos_obrigatorios_texto;
      }

      Object.keys(parametros).forEach((key) => {
        if (parametros[key] === undefined) {
          delete parametros[key];
        }
      });

      return parametros;
    },
    async simularAutomacao() {
      if (!this.automacaoForm.gatilho) {
        this.$toast.error("Selecione um gatilho para simular.");
        return;
      }

      if (!this.automacaoSimulacao.id_crm_oportunidade) {
        this.$toast.error("Informe o ID da oportunidade.");
        return;
      }

      this.saving = true;

      try {
        const { data } = await crmService.executarAutomacao({
          gatilho: this.automacaoForm.gatilho,
          origem: "manual",
          simulacao: true,
          chave_evento: `simulacao:${Date.now()}`,
          payload: {
            id_crm_oportunidade: this.automacaoSimulacao.id_crm_oportunidade,
            simulacao: true,
          },
        });

        if (data && data.status === false) {
          this.$toast.error(data.msg || "Não foi possível simular.");
          return;
        }

        this.automacaoSimulacao.resultado = data.data || data;
        this.$toast.success("Simulação executada.");
        await this.loadAutomacaoExecucoes();
      } catch (error) {
        this.$toast.error(
          this.mensagemErro(error, "Não foi possível simular.")
        );
      } finally {
        this.saving = false;
      }
    },
    async loadAutomacaoExecucoes() {
      try {
        const response = await crmService.automacaoExecucoes({ per_page: 8 });
        this.automacaoExecucoes = this.normalizarColecao(response);
      } catch (error) {
        this.automacaoExecucoes = [];
        this.$toast.error("Não foi possível carregar o histórico.");
      }
    },
    opcoesAutomacaoSelect(tipo) {
      const opcoes = this.automacaoOpcoes[tipo] || [];

      return opcoes.map((item) => ({
        value: item.chave,
        text: item.nome || item.chave,
      }));
    },
    automacaoGatilhoResumo(automacao) {
      const gatilho =
        automacao.gatilhos && automacao.gatilhos.length
          ? automacao.gatilhos[0].gatilho
          : "";

      return this.automacaoGatilhoLabel(gatilho);
    },
    automacaoAcaoResumo(automacao) {
      const acoes = (automacao.acoes || [])
        .filter((acao) => acao.ativo !== false)
        .map((acao) => {
          const option = this.automacaoAcaoOptions.find(
            (item) => item.value === acao.acao
          );

          return option ? option.text : acao.acao;
        })
        .filter(Boolean);

      return acoes.length ? `Ações: ${acoes.join(", ")}` : "Sem ações";
    },
    automacaoGatilhoLabel(gatilho) {
      const option = this.automacaoGatilhoOptions.find(
        (item) => item.value === gatilho
      );

      return option ? option.text : gatilho || "Sem gatilho";
    },
    execucaoStatusVariant(status) {
      if (status === "sucesso") return "success";
      if (status === "erro") return "danger";
      if (status === "ignorada") return "warning";
      return "secondary";
    },
    valorCondicaoTexto(valor) {
      if (valor && typeof valor === "object" && "valor" in valor) {
        return valor.valor;
      }

      return valor || "";
    },
    jsonTexto(valor) {
      if (!valor) {
        return "{}";
      }

      try {
        return JSON.stringify(valor, null, 2);
      } catch (error) {
        return "{}";
      }
    },
    parseJsonSeguro(texto) {
      const value = String(texto || "").trim();

      if (!value) {
        return {};
      }

      try {
        return JSON.parse(value);
      } catch (error) {
        return false;
      }
    },
    formatDateTime(value) {
      if (!value) {
        return "-";
      }

      return new Date(value).toLocaleString("pt-BR");
    },
    etapaAutomacaoResumo(etapa) {
      const partes = [];

      if (etapa.sugerir_tarefa) {
        partes.push("sugere tarefa");
      }

      if (etapa.destacar_sem_tarefa) {
        partes.push("destaca sem tarefa");
      }

      if (etapa.alertar_oportunidade_parada) {
        partes.push(`alerta ${etapa.alerta_parada_dias || 7}d parada`);
      }

      if (etapa.exigir_motivo_perda) {
        partes.push("exige motivo");
      }

      return partes.length ? `Automação: ${partes.join(" · ")}` : "";
    },
    etapaCamposObrigatoriosResumo(etapa) {
      const obrigatorios = (etapa.campos_obrigatorios || []).filter(
        (item) => item.ativo !== false && item.campo
      );

      if (!obrigatorios.length) {
        return "";
      }

      const campos = obrigatorios
        .map((item) => item.campo.nome)
        .filter(Boolean)
        .slice(0, 4)
        .join(", ");

      return `Campos obrigatórios: ${campos}`;
    },
    proximaOrdemTemperatura() {
      return (
        this.temperaturas.reduce(
          (maior, item) => Math.max(maior, Number(item.peso || 0)),
          0
        ) + 1
      );
    },
    proximaOrdemCampoComercial() {
      return (
        this.camposComerciais.reduce(
          (maior, item) => Math.max(maior, Number(item.ordem || 0)),
          0
        ) + 1
      );
    },
    tipoCampoLabel(tipo) {
      const option = this.tipoCampoOptions.find((item) => item.value === tipo);

      return option ? option.text : tipo || "Texto";
    },
    slug(value) {
      return String(value || "item")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    },
    async executar(request, mensagem) {
      this.saving = true;

      try {
        const response = await request;
        const body = response && response.data ? response.data : response;

        if (body && body.status === false) {
          this.$toast.error(body.msg || "Não foi possível salvar.");
          return null;
        }

        this.$toast.success(mensagem);
        await this.loadAll();

        return body && body.data ? body.data : body;
      } catch (error) {
        this.$toast.error(this.mensagemErro(error, "Não foi possível salvar."));
        return null;
      } finally {
        this.saving = false;
      }
    },
    mensagemErro(error, padrao) {
      const response = error && error.response ? error.response.data : null;

      return (response && (response.msg || response.message)) || padrao;
    },
  },
};
</script>

<style scoped>
.crm-page-header {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 20px;
}

.crm-page-header > div:first-child {
  min-width: 0;
}

.crm-page-header h2,
.crm-page-header .text-muted {
  overflow-wrap: anywhere;
}

.crm-header-actions,
.crm-panel-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.crm-header-actions .btn,
.crm-panel-actions .btn {
  white-space: normal;
}

.crm-config-tabs {
  border-bottom: 1px solid #e4e6ef;
  flex-wrap: nowrap;
  overflow-x: auto;
}

.crm-config-tabs::v-deep .nav-link {
  white-space: nowrap;
}

.crm-panel {
  background: #ffffff;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  padding: 20px;
}

.crm-panel-header {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 16px;
}

.crm-panel-header h3 {
  color: #3f4254;
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}

.crm-list-item {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  color: #3f4254;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  min-height: 66px;
  min-width: 0;
  padding: 12px;
  text-align: left;
  width: 100%;
}

.crm-list-item > span:first-child {
  min-width: 0;
}

.crm-list-item strong {
  overflow-wrap: anywhere;
}

.crm-list-item--automation {
  align-items: flex-start;
  gap: 12px;
}

.crm-list-item-main {
  background: transparent;
  border: 0;
  color: inherit;
  flex: 1;
  min-width: 0;
  padding: 0;
  text-align: left;
}

.crm-list-item-main > span {
  display: block;
  min-width: 0;
}

.crm-automation-controls {
  align-items: flex-end;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 10px;
}

.crm-automation-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}

.crm-list-item.active {
  border-color: #3699ff;
  box-shadow: 0 0 0 2px rgba(54, 153, 255, 0.12);
}

.crm-list-item small,
.crm-stage-main small {
  color: #7e8299;
  display: block;
  line-height: 1.35;
  margin-top: 3px;
}

.crm-badges {
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 6px;
  margin-left: 10px;
}

.crm-empty {
  align-items: flex-start;
  background: #f8f9fb;
  border: 1px dashed #d7dae7;
  border-radius: 8px;
  color: #7e8299;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 120px;
  padding: 20px;
}

.crm-stage-list {
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  overflow: hidden;
}

.crm-stage-row {
  align-items: center;
  border-bottom: 1px solid #e4e6ef;
  display: flex;
  gap: 10px;
  min-height: 64px;
  padding: 12px;
}

.crm-stage-row:last-child {
  border-bottom: 0;
}

.crm-color {
  border-radius: 6px;
  display: block;
  flex: 0 0 22px;
  height: 22px;
  margin-right: 12px;
  width: 22px;
}

.crm-stage-main {
  flex: 1;
  min-width: 0;
}

.crm-stage-actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 10px;
}

.crm-access-box {
  background: #f8f9fb;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  margin-top: 18px;
  padding: 16px;
}

.crm-access-header {
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 12px;
}

.crm-access-header h4 {
  color: #3f4254;
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 4px;
}

.crm-access-header small {
  color: #7e8299;
  display: block;
  line-height: 1.35;
}

.crm-access-options {
  background: #ffffff;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  max-height: 220px;
  overflow-y: auto;
  padding: 12px 14px;
}

.crm-assignments-modal::v-deep .modal-xl {
  max-width: 1120px;
  width: calc(100vw - 32px);
}

.crm-presets-modal::v-deep .modal-lg {
  max-width: 920px;
  width: calc(100vw - 32px);
}

.crm-presets {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.crm-presets-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.crm-preset-card {
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 16px;
}

.crm-preset-card__header {
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.crm-preset-card h4 {
  color: #3f4254;
  font-size: 16px;
  font-weight: 800;
  margin: 0 0 4px;
}

.crm-preset-card p {
  color: #7e8299;
  line-height: 1.35;
  margin: 0;
}

.crm-preset-card ul {
  color: #3f4254;
  line-height: 1.45;
  margin: 0;
  padding-left: 18px;
}

.crm-preset-card .btn {
  align-self: flex-start;
}

.crm-assignments {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.crm-assignments-intro {
  align-items: flex-start;
  background: #f8f9fb;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 14px 16px;
}

.crm-assignments-intro strong,
.crm-assignments-intro span {
  display: block;
}

.crm-assignments-intro span {
  color: #7e8299;
  line-height: 1.35;
  margin-top: 3px;
}

.crm-role-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.crm-role-section {
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  min-width: 0;
  padding: 14px;
}

.crm-role-header {
  margin-bottom: 10px;
  min-height: 54px;
}

.crm-role-header h4 {
  color: #3f4254;
  font-size: 15px;
  font-weight: 800;
  margin: 0 0 4px;
}

.crm-role-header small {
  color: #7e8299;
  display: block;
  line-height: 1.35;
}

.crm-role-checks {
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  max-height: 220px;
  overflow: auto;
  padding: 8px 10px;
}

.crm-role-checks::v-deep .custom-control {
  margin: 0;
  padding-bottom: 7px;
  padding-top: 7px;
}

.crm-role-checks::v-deep .custom-control-label {
  color: #3f4254;
  font-weight: 700;
}

.crm-modal-actions {
  border-top: 1px solid #e4e6ef;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 16px;
}

.crm-chip-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
}

.crm-chip {
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  color: #3f4254;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  min-height: 76px;
  min-width: 0;
  padding: 12px;
  text-align: left;
}

.crm-chip > span:first-child {
  flex: 1 1 150px;
  min-width: 0;
}

.crm-chip strong {
  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-height: 1.25;
  overflow: hidden;
  overflow-wrap: normal;
  text-overflow: ellipsis;
  word-break: normal;
}

.crm-chip small {
  color: #7e8299;
  display: block;
  margin-top: 3px;
  overflow-wrap: anywhere;
}

.crm-chip .crm-badges {
  justify-content: flex-end;
  margin-left: 0;
}

.crm-chip:hover,
.crm-list-item:hover {
  border-color: #b5b5c3;
}

.crm-dot {
  border-radius: 999px;
  display: inline-block;
  height: 10px;
  margin-right: 8px;
  width: 10px;
}

.crm-automation-box {
  background: #f8f9fb;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  padding: 16px;
}

.crm-automation-box h4 {
  color: #3f4254;
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 12px;
}

.crm-config-alert {
  border: 1px solid #e4e6ef;
  color: #5e6278;
}

.crm-builder-section {
  border-top: 1px solid #e4e6ef;
  margin-top: 18px;
  padding-top: 18px;
}

.crm-builder-title {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin-bottom: 12px;
}

.crm-builder-title h4 {
  color: #3f4254;
  font-size: 15px;
  font-weight: 800;
  margin: 0;
}

.crm-builder-row {
  align-items: flex-start;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.85fr) minmax(0, 1fr) auto;
  margin-bottom: 10px;
}

.crm-action-builder {
  background: #f8f9fb;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  margin-bottom: 12px;
  padding: 14px;
}

.crm-action-builder .crm-builder-row {
  grid-template-columns: minmax(0, 1fr) auto;
}

.crm-automation-editor {
  display: grid;
  gap: 12px;
}

.crm-action-box {
  background: #ffffff;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  padding: 14px;
}

.crm-action-label {
  color: #181c32;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  margin-bottom: 10px;
  text-transform: uppercase;
}

.crm-advanced-json {
  background: #ffffff;
  border: 1px dashed #d7dae7;
  border-radius: 8px;
  padding: 10px 12px;
}

.crm-advanced-json summary {
  color: #7e8299;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 8px;
}

.crm-builder-footer {
  align-items: center;
  border-top: 1px solid #e4e6ef;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 18px;
  padding-top: 18px;
}

.crm-empty.compact {
  min-height: 72px;
  padding: 14px;
}

.crm-simulation-result {
  background: #f3f6f9;
  border-radius: 8px;
  color: #3f4254;
  margin-top: 14px;
  padding: 12px;
}

.crm-simulation-result small {
  color: #7e8299;
  display: block;
  margin-top: 4px;
}

.crm-execution-list {
  display: grid;
  gap: 10px;
}

.crm-execution-row {
  align-items: flex-start;
  background: #f8f9fb;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  padding: 12px;
}

.crm-execution-row strong,
.crm-execution-row small {
  display: block;
  overflow-wrap: anywhere;
}

.crm-execution-row small {
  color: #7e8299;
  margin-top: 3px;
}

@media (max-width: 767px) {
  .crm-page-header {
    display: block;
  }

  .crm-header-actions,
  .crm-panel-actions {
    justify-content: flex-start;
    margin-top: 12px;
  }

  .crm-panel-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }

  .crm-list-item--automation {
    flex-direction: column;
  }

  .crm-list-item-main,
  .crm-automation-controls {
    width: 100%;
  }

  .crm-automation-controls {
    align-items: flex-start;
  }

  .crm-automation-buttons {
    justify-content: flex-start;
  }

  .crm-stage-row {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 8px;
  }

  .crm-stage-row .btn {
    margin-left: 0 !important;
  }

  .crm-stage-actions {
    justify-content: flex-start;
    width: 100%;
  }

  .crm-assignments-intro,
  .crm-modal-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .crm-role-grid {
    grid-template-columns: 1fr;
  }

  .crm-builder-row,
  .crm-action-builder .crm-builder-row {
    grid-template-columns: 1fr;
  }

  .crm-builder-footer,
  .crm-builder-title,
  .crm-execution-row {
    align-items: stretch;
    flex-direction: column;
  }
}

@media (max-width: 575px) {
  .crm-header-actions .btn,
  .crm-panel-actions .btn {
    flex: 1 1 100%;
  }

  .crm-panel {
    padding: 16px;
  }

  .crm-list-item,
  .crm-chip {
    align-items: flex-start;
    flex-direction: column;
  }

  .crm-badges {
    margin-left: 0;
  }
}
</style>
